<template lang="pug" src="./client-bonuses-editor.pug"> </template>
<style lang="scss" src="./client-bonuses-editor.scss" scoped> </style>

<script>
import _ from 'lodash';
import { mapState, mapGetters } from 'vuex';
import { validationMixin } from 'vuelidate';
import { required, numeric, minValue } from 'vuelidate/lib/validators';
import validate from '@/mixins/validate';
import request from '@/utils/request';

export default {
    mixins: [validationMixin, validate],
    data: () => ({
        bonusUseLimit: {
            title: '',
            description: '',
            bonusFrequency: '',
            bonusValue: '',
            bonusValueFromDriver: '',
            startDate: new Date().format('{yyyy}-{MM}-{dd}'),
            endDate: new Date().format('{yyyy}-{MM}-{dd}'),
            isActive: false
        },
        users: [],
        bonuses: {
            amount: '',
            smsText: null,
            transferBonuses: false,
            smsSend: false,
            smsTransliteration: false
        },
        phones: '',
        validatePhonesObj: {
            duplicate: {
                array: [],
                value: null
            },
            notFound: {
                array: [],
                value: null
            },
            otherBonusSystem: {
                array: [],
                value: null
            },
            success: {
                array: [],
                value: null
            },
            validationError: {
                array: [],
                value: null
            }
        },
        addBonusesResults: {
            success: [],
            notFound: [],
            duplicate: [],
            validationError: []
        },
        phonesIsValid: false,
        canSaveBonusUseLimit: false,
        addBonusesProgress: false,
        showAddBonusesResults: false,
        brandId: null,
        loaded: false,
        pagination: {
            page: 1,
            totalPages: 1
        },
        startDateModal: false,
        endDateModal: false,
        removeModal: false,
        requestProcessing: false
    }),
    validations: {
        brandId: {
            required
        },
        bonusUseLimit: {
            title: {
                required
            },
            bonusFrequency: {
                required,
                minValue: minValue(1),
                numeric
            },
            bonusValue: {
                required,
                minValue: minValue(0.1),
                demical: true
            },
            bonusValueFromDriver: {
                minValue: function(value) {
                    if (!value) {
                        return true;
                    }

                    return !isNaN(value - parseFloat(value));
                },
                maxValue: function(value) {
                    return Number(value) <= Number(this.bonusUseLimit.bonusValue);
                }
            },
            startDate: {
                required
            },
            endDate: {
                required
            }
        },
        phones: {
            required
        },
        bonuses: {
            amount: {
                required,
                minValue: minValue(1),
                numeric
            }
        }
    },
    computed: {
        ...mapState({
            brands: state => state.data.brands
        }),
        ...mapGetters(['userBrandId']),
        saveMethod() {
            return this.bonusUseLimit.id ? 'put' : 'post';
        },
        saveUrl() {
            const bonusId = this.bonusUseLimit.id;

            return bonusId ? `/admin/bonuses/clients/bonusUseLimits/${bonusId}`
                : '/admin/bonuses/clients/bonusUseLimits';
        },
        minStartDate() {
            return new Date().format('{yyyy}-{MM}-{dd}');
        },
        minEndDate() {
            if (!this.bonusUseLimit.startDate) {
                return this.minStartDate;
            }

            return new Date(this.bonusUseLimit.startDate).format('{yyyy}-{MM}-{dd}');
        },
        headers() {
            return [
                'phone', 'refillBalance', 'useBalance', 'leftBalance',
                'revertBalance', 'orderCount', 'bonusOrderCount'
            ].map(it => ({
                text: this.$vuetify.lang.t(`$vuetify.pages.Bonuses.info.${it}`),
                value: it,
                sortable: false,
                align: 'center'
            }));
        },
        canAddBonuses() {
            const successValue = [this.validatePhonesObj.success.value, this.validatePhonesObj.otherBonusSystem.value].join('\n');

            return this.phones.trim() == successValue.trim();
        },
        sumOfBonuses() {
            return _.sum(this.addBonusesResults.success.map(it => +it.balance));
        },
        sumOfTransferBalance() {
            return _.sum(this.addBonusesResults.success.map(it => it.transferBalance));
        }
    },
    watch: {
        bonusUseLimit: {
            handler() {
                this.canSaveBonusUseLimit = true;
            },
            deep: true
        },
        brandId() {
            this.canSaveBonusUseLimit = true;
        }
    },
    created() {
        this.$store.dispatch('data/getBrands');

        if (this.userBrandId) {
            this.brandId = this.userBrandId;
        }

        if (this.$route.params.id) {
            this.loaded = true;
            this.loadData();
        }
    },
    methods: {
        async loadData(page = 1) {
            await request({
                method: 'get',
                url: `/admin/bonuses/clients/bonusUseLimits/${this.$route.params.id}`,
                params: { page }
            }).then(res => this.successLoad(res)).catch(error => {
                if(error.response && error.response.data && error.response.data.error) {
                    this.$store.dispatch('alert/addMessage', {
                        message: error.response.data.error
                    });
                }
            });
        },

        successLoad(res) {
            this.pagination = {
                page: res.data.page || 1,
                totalPages: res.data.totalPages || 1
            };

            const data = res.data.bonusUseLimit || res.data;

            data.bonusValue = +data.bonusValue / 100;
            data.bonusValueFromDriver = +data.bonusValueFromDriver / 100;

            if (data.brandId) {
                this.brandId = data.brandId;
            }

            if (data.users) {
                this.users = data.users;
            }

            this.bonusUseLimit = Object.assign({}, this.bonusUseLimit,
                Object.reject(data, ['users']), {
                startDate: new Date(data.startDate).format('{yyyy}-{MM}-{dd}'),
                endDate: new Date(data.endDate).format('{yyyy}-{MM}-{dd}'),
            });
            this.bonuses.smsText = this.generateSmsText();

            if (this.bonusUseLimit.id && !this.$route.params.id) {
                this.$router.push(`/bonuses/client/editor/${this.bonusUseLimit.id}`);
            }

            this.$nextTick(() => {
                this.canSaveBonusUseLimit = false;
                this.loaded = false;
            });
        },

        async saveBonusUseLimit() {
            if (this.$v.bonusUseLimit.$invalid || this.$v.brandId.$invalid) {
                this.$v.bonusUseLimit.$touch();
                this.$v.brandId.$touch();

                this.$nextTick(() => this.focusFirstErrorInput());
                return;
            }

            if (this.requestProcessing) return;

            this.requestProcessing = true;

            const data = Object.assign(this.bonusUseLimit, {
                brandId: this.brandId
            });

            await request({ method: this.saveMethod, url: this.saveUrl, data })
                .then(res => {
                    this.successLoad(res);
                    this.$store.dispatch('alert/addMessage', {
                        type: 'success',
                        message: 'success'
                    });
                }).catch(error => {
                    if(error.response && error.response.data && error.response.data.error) {
                        this.$store.dispatch('alert/addMessage', {
                            message: error.response.data.error
                        });
                    }
                }).finally(() => {
                    this.$nextTick(() => {
                        this.requestProcessing = false;
                    });
                });
        },

        async validatePhonesForm() {
            this.showAddBonusesResults = false;

            if (this.$v.phones.$invalid || this.$v.brandId.$invalid) {
                this.$v.phones.$touch();
                this.$v.brandId.$touch();

                return this.$nextTick(() => this.focusFirstErrorInput());
            }

            if (this.requestProcessing) return;

            this.requestProcessing = true;

            const data = {
                phones: this.phones.split('\n').filter(it => !!it),
                brandId: this.brandId
            };

            this.bonuses.smsText = this.generateSmsText();

            await request({
                method: 'post',
                url: '/admin/bonuses/clients/validatePhones',
                data
            }).then(res => {
                if (res.status == 200) {
                    const data = Object.keys(this.validatePhonesObj).map(item => ({
                        [item]: {
                            array: res.data[item],
                            value: res.data[item].length ? res.data[item].map('phone').join('\n') : ''
                        }
                    }));

                    this.phonesIsValid = true;
                    this.validatePhonesObj = Object.assign({}, ...data);
                    this.phones = [this.validatePhonesObj.success.value, this.validatePhonesObj.otherBonusSystem.value].join('\n').trim();

                    this.$store.dispatch('alert/addMessage', {
                        type: 'success',
                        message: 'success'
                    });
                }
            }).finally(() => {
                this.$nextTick(() => {
                    this.requestProcessing = false;
                });
            });
        },

        changeSmsText() {
            this.bonuses.smsText = this.generateSmsText();
        },

        generateSmsText() {
            return this.$vuetify.lang.t('$vuetify.pages.Bonuses.editor.generatedSmsText')
                .replace(/:startDate/g, new Date(this.bonusUseLimit.startDate).format('{yyyy}-{MM}-{dd}'))
                .replace(/:endDate/g, new Date(this.bonusUseLimit.endDate).format('{yyyy}-{MM}-{dd}'))
                .replace(/:amount/g, Number(this.bonuses.amount))
                .replace(/:bonusValue/g, Number(this.bonusUseLimit.bonusValue))
                .replace(/:bonusFrequency/g, Number(this.bonusUseLimit.bonusFrequency));
        },

        clearAddBonusesForms() {
            this.bonuses = {
                amount: null,
                smsText: null,
                transferBonuses: false,
                smsSend: false,
                smsTransliteration: false
            };

            this.phones = '';

            this.validatePhonesObj = {
                duplicate: {
                    array: [],
                    value: ''
                },
                notFound: {
                    array: [],
                    value: ''
                },
                otherBonusSystem: {
                    array: [],
                    value: ''
                },
                success: {
                    array: [],
                    value: ''
                },
                validationError: {
                    array: [],
                    value: ''
                }
            };

            this.phonesIsValid = false;
            this.addBonusesProgress = false;
        },

        async addBonuses() {
            if (this.$v.phones.$invalid || this.$v.brandId.$invalid || this.$v.bonuses.$invalid) {
                this.$v.phones.$touch();
                this.$v.brandId.$touch();
                this.$v.bonuses.$touch();

                return this.$nextTick(() => this.focusFirstErrorInput());
            }

            if (this.requestProcessing) return;

            this.requestProcessing = true;

            const phones = this.phones.split('\n').filter(it => !!it);

            this.addBonusesProgress = true;

            while (phones.length) {
                const data = Object.assign(this.bonuses, {
                    phones: phones.splice(0, 100),
                    brandId: this.brandId,
                    bonusUseLimitId: this.bonusUseLimit.id
                });

                await request({ method: 'post', url: '/admin/bonuses/clients', data }).then(res => {
                    for (let key of Object.keys(this.addBonusesResults)) {
                        this.addBonusesResults[key] = this.addBonusesResults[key].concat(res.data[key]);
                    }
                });
            }

            this.$nextTick(() => {
                this.requestProcessing = false;
            });

            this.loadData();
            this.clearAddBonusesForms();

            this.$store.dispatch('alert/addMessage', {
                type: 'success',
                message: 'success'
            });

            this.showAddBonusesResults = true;
        },

        addBonusesAgain() {
            this.showAddBonusesResults = false;

            this.addBonusesResults = {
                success: [],
                notFound: [],
                duplicate: [],
                validationError: []
            };

            this.$v.phones.$reset();
            this.$v.bonuses.$reset();
        },

        changeStartDate() {
            if (this.bonusUseLimit.startDate > this.bonusUseLimit.endDate) {
                this.bonusUseLimit.endDate = this.bonusUseLimit.startDate;
            }
        },

        async remove() {
            if (this.loaded) return;

            this.loaded = true;

            await request({ method: 'delete', url: `/admin/bonuses/clients/bonusUseLimits/${this.bonusUseLimit.id}` })
                .then(res => {
                    if(res.status == 200) {
                        this.$router.push('/bonuses/client');
                    }
                }).catch(error => {
                    if(error.response && error.response.data && error.response.data.error) {
                        this.$store.dispatch('alert/addMessage', {
                            message: error.response.data.error
                        });
                    }
                }).finally(() => {
                    this.$nextTick(() => {
                        this.loaded = false;
                    });
                });
        }
    }
};
</script>
